/**
 * Client Attributes state management
 */
import { createAction, getType } from "typesafe-actions";

import api from "../../services/api";

import { ClientAttribute, ClientAttributeInstance } from "../../types/models";
import { ThunkResult } from "../../types/redux";
import { RootAction } from "../actions";
import { DEFAULT_CLIENT_ATTRIBUTE_NAME } from "src/components/organisms/Dimensions/ClientAttributeColumn";

export type ClientAttributesState = ClientAttribute[];

// Initial state
const initialState: ClientAttributesState = [];

// Actions
export const clientAttributeActions = {
  setClientAttributes: createAction("@clientAttributes/setClientAttributes")<
    ClientAttribute[]
  >(),
  addClientAttribute: createAction(
    "@clientAttributes/addClientAttribute"
  )<{Name: string}>(),
  clearUnsavedClientAttributes: createAction("@clientAttributes/clearUnsavedClientAttributes")(),
  setClientAttributeInstances: createAction(
    "@clientAttributes/setClientAttributeInstances"
  )<{
    clientAttributeId: number;
    instances: ClientAttributeInstance[];
  }>(),
  addClientAttributeInstance: createAction(
    "@clientAttributes/addClientAttributeInstance"
  )<{
    clientAttributeId: number;
    instance: ClientAttributeInstance;
  }>(),
};

// Reducer
export function clientAttributesReducer(
  state = initialState,
  action: RootAction
): ClientAttributesState {
  switch (action.type) {
    case getType(clientAttributeActions.setClientAttributes):
      return (action as any).payload;
    case getType(clientAttributeActions.clearUnsavedClientAttributes):
      return [
        ...state.filter(a => a.Name !== DEFAULT_CLIENT_ATTRIBUTE_NAME),
      ];
    case getType(clientAttributeActions.addClientAttribute):
      return [
        ...state,
        (action as any).payload,
      ];
    case getType(clientAttributeActions.setClientAttributeInstances):
      const { clientAttributeId, instances } = (action as any).payload;
      return state.map((attr) =>
        attr.id === clientAttributeId
          ? { ...attr, client_attribute_instances: instances }
          : attr
      );
    case getType(clientAttributeActions.addClientAttributeInstance):
      const { clientAttributeId: attrId, instance } = (action as any).payload;
      return state.map((attr) =>
        attr.id === attrId
          ? {
              ...attr,
              client_attribute_instances: [
                instance,
                ...(attr.client_attribute_instances || []),
              ],
            }
          : attr
      );
    default:
      return state;
  }
}

// Action Creators / Thunks
export const getClientAttributesData =
  (clientId: number): ThunkResult<Promise<any>> =>
  (dispatch) => {
    return api
      .getClientAttributes(clientId)
      .then((data) => {
        dispatch(clientAttributeActions.setClientAttributes(data));
        return data;
      })
      .catch((error) => {
        console.error("Error fetching client attributes:", error);
        throw error;
      });
  };

export const createClientAttributeData =
  (clientId: number, name: string): ThunkResult<Promise<any>> =>
  (dispatch) => {
    return api
      .createClientAttribute(clientId, name)
      .then((data) => {
        dispatch(clientAttributeActions.clearUnsavedClientAttributes());
        dispatch(clientAttributeActions.addClientAttribute(data));
        return data;
      })
      .catch((error) => {
        console.error("Error creating client attribute:", error);
        throw error;
      });
  };

export const getClientAttributeInstancesData =
  (clientId: number, clientAttributeId: number): ThunkResult<Promise<any>> =>
  (dispatch) => {
    return api
      .getClientAttributeInstances(clientId, clientAttributeId)
      .then((data) => {
        dispatch(
          clientAttributeActions.setClientAttributeInstances({
            clientAttributeId,
            instances: data,
          })
        );
        return data;
      })
      .catch((error) => {
        console.error("Error fetching client attribute instances:", error);
        throw error;
      });
  };

export const createClientAttributeInstanceData =
  (
    clientId: number,
    clientAttributeId: number,
    name: string
  ): ThunkResult<Promise<any>> =>
  (dispatch) => {
    return api
      .createClientAttributeInstance(clientId, clientAttributeId, name)
      .then((data) => {
        dispatch(
          clientAttributeActions.addClientAttributeInstance({
            clientAttributeId,
            instance: data,
          })
        );
        return data;
      })
      .catch((error) => {
        console.error("Error creating client attribute instance:", error);
        throw error;
      });
  };
